import { apiBaseUrl } from '../apiConsts';
import { buildPaginationQuery, MAX_PAGE_SIZE } from '../pagination';
import {
  getDataWithRetry,
  mapPagedResult,
  sendWithoutBody,
} from '../fetchUtils';
import {
  isValidTemplateResponse,
  isValidTemplatesResponse,
} from '../apiValidators';

import { getDefaultError } from '../../utils';
import type {
  TrainingRoutineTemplate,
  WithOptionalId,
  PagedResponse,
  PaginatedListParams,
} from '../../types';
import type { FetchResult, FetchSuccess } from '../fetchUtils';

export const createNewTemplate = async (
  template: WithOptionalId<TrainingRoutineTemplate>,
  trainerId: string,
): Promise<FetchResult<TrainingRoutineTemplate | null>> => {
  if (!trainerId) {
    return {
      data: null,
      success: false,
      error: getDefaultError('Trainer id must be provided'),
    };
  }

  const { trainerIds, createdDate, updatedDate, exerciseCount, ...payload } = template;

  const createResponse = await getDataWithRetry<TrainingRoutineTemplate | null>(
    `${apiBaseUrl}training-routine-templates`,
    {
      headers: {
        'content-type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify(payload),
    },
    isValidTemplateResponse,
  );
  if (!createResponse.success || !createResponse.data) {
    return {
      success: false,
      data: null,
      error:
        createResponse.error ?? getDefaultError('Failed to create template'),
      status: createResponse.status,
      durationMs: createResponse.durationMs,
    };
  }

  const associateResult = await sendWithoutBody(
    `${apiBaseUrl}trainers/${trainerId}/associate/training-routine-template`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify([createResponse.data.id]),
    },
  );
  if (!associateResult.success) {
    return associateResult;
  }
  return {
    ...createResponse,
    data: {
      ...createResponse.data,
      trainerIds: [trainerId],
    },
  };
};

export const fetchTemplateById = (
  templateId: string,
): Promise<FetchResult<TrainingRoutineTemplate | null>> => {
  if (!templateId) {
    return Promise.resolve({
      data: null,
      success: false,
      error: getDefaultError('Template not found'),
    });
  }
  return getDataWithRetry(
    `${apiBaseUrl}training-routine-templates/${templateId}`,
    {},
    isValidTemplateResponse,
  );
};

export const updateTemplateData = (
  template: TrainingRoutineTemplate,
): Promise<FetchResult<TrainingRoutineTemplate | null>> => {
  const { createdDate, updatedDate, trainerIds, exerciseCount, ...payload } = template;

  return getDataWithRetry(
    `${apiBaseUrl}training-routine-templates`,
    {
      headers: {
        'content-type': 'application/json',
      },
      method: 'PUT',
      body: JSON.stringify(payload),
    },
    isValidTemplateResponse,
  );
};

export const deleteTemplateById = (
  templateId: string,
): Promise<FetchResult<null>> => {
  return sendWithoutBody(
    `${apiBaseUrl}training-routine-templates/${templateId}`,
    {
      method: 'DELETE',
    },
  );
};

export const fetchTrainerTemplates = async (
  trainerId: string,
): Promise<FetchResult<TrainingRoutineTemplate[]>> => {
  const response = await getDataWithRetry<
    PagedResponse<TrainingRoutineTemplate>
  >(
    `${apiBaseUrl}training-routine-templates/trainer/${trainerId}?page=0&size=${MAX_PAGE_SIZE}&sort=name`,
    {},
    isValidTemplatesResponse,
  );
  return mapPagedResult(response);
};

export const fetchTemplatesPageByTrainerId = (
  trainerId: string,
  params: PaginatedListParams,
): Promise<FetchResult<PagedResponse<TrainingRoutineTemplate>>> => {
  return getDataWithRetry<PagedResponse<TrainingRoutineTemplate>>(
    `${apiBaseUrl}training-routine-templates/trainer/${trainerId}?${buildPaginationQuery(params)}`,
    {},
    isValidTemplatesResponse,
  );
};

export const fetchTemplatesPageByGymId = (
  gymId: string,
  params: PaginatedListParams,
): Promise<FetchResult<PagedResponse<TrainingRoutineTemplate>>> => {
  return getDataWithRetry<PagedResponse<TrainingRoutineTemplate>>(
    `${apiBaseUrl}training-routine-templates/gym/${gymId}?${buildPaginationQuery(params)}`,
    {},
    isValidTemplatesResponse,
  );
};

export const fetchTemplatesByIds = async (
  ids: string[],
): Promise<FetchResult<PagedResponse<TrainingRoutineTemplate>>> => {
  const BATCH_SIZE = 100;
  const batches = [];

  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    batches.push(ids.slice(i, i + BATCH_SIZE));
  }

  const results = await Promise.all(
    batches.map((batch) =>
      getDataWithRetry<PagedResponse<TrainingRoutineTemplate>>(
        `${apiBaseUrl}training-routine-templates/getByIds?page=0&size=${BATCH_SIZE}`,
        {
          headers: { 'content-type': 'application/json' },
          method: 'POST',
          body: JSON.stringify(batch),
        },
        isValidTemplatesResponse,
      ),
    ),
  );

  const failed = results.find((r) => !r.success);
  if (failed) return failed;

  const allContent = results.flatMap((r) => r.success ? r.data.content : []);

  return {
    ...results[0],
    data: {
      ...results[0].data,
      content: allContent,
    },
  } as FetchSuccess<PagedResponse<TrainingRoutineTemplate>>;
};